// src/components/crm/CurrentUserProfile.js
import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase/config';
import { doc, updateDoc } from 'firebase/firestore';
// emailself-1a2b3c4d — self-service email change: verifyBeforeUpdateEmail + reauth
import { verifyBeforeUpdateEmail, reauthenticateWithCredential, EmailAuthProvider, sendPasswordResetEmail } from 'firebase/auth';

const CurrentUserProfile = ({ user, profile }) => {
    const [name, setName] = useState(profile?.name || '');
    const [email, setEmail] = useState(user?.email || '');
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');
    // emailself-2b3c4d5e — message color (errors red / success green) + inline reauth prompt state
    const [isError, setIsError] = useState(false);
    const [needsReauth, setNeedsReauth] = useState(false);
    const [reauthPassword, setReauthPassword] = useState('');

    // THE FIX:
    useEffect(() => {
        setName(profile?.name || '');
        setEmail(user?.email || '');
    }, [profile?.name, user?.email]); // <-- DEPEND ON PRIMITIVE VALUES

    // emailself-3c4d5e6f — split name/email writes; verifyBeforeUpdateEmail; reauth branch
    const handleProfileUpdate = async () => {
        setIsSaving(true);
        setMessage('');
        setIsError(false);
        setNeedsReauth(false);

        // Step 1 — save the name on its own. It must never depend on the email step,
        // so if it fails we stop here and report; if it succeeds the name is persisted
        // regardless of what happens with the email below.
        try {
            const userDocRef = doc(db, 'users', user.uid);
            await updateDoc(userDocRef, { name });
        } catch (error) {
            console.error("Error saving name:", error);
            setMessage(`Error saving name: ${error.message}`);
            setIsError(true);
            setIsSaving(false);
            return;
        }

        // Step 2 — email change (only if it actually changed). verifyBeforeUpdateEmail
        // sends a confirmation link to the NEW address; the email is not swapped until
        // the user clicks that link. Firebase requires a recent login for this — if it's
        // stale we surface an inline password prompt instead of failing outright.
        if (email !== user.email) {
            try {
                await verifyBeforeUpdateEmail(auth.currentUser, email);
                setMessage(`Name saved. A confirmation link was sent to ${email} — click it to finish changing your email.`);
                setIsError(false);
            } catch (error) {
                if (error.code === 'auth/requires-recent-login') {
                    setNeedsReauth(true);
                    setMessage('For your security, please re-enter your password to change your email address.');
                    setIsError(true);
                    setIsSaving(false);
                    return;
                }
                console.error("Error updating email:", error);
                setMessage(`Your name was saved, but the email change failed: ${error.message}`);
                setIsError(true);
                setIsSaving(false);
                return;
            }
        } else {
            setMessage('Profile updated successfully!');
            setIsError(false);
            setTimeout(() => setMessage(''), 3000);
        }

        setIsSaving(false);
    };

    // emailself-4d5e6f70 — reauthenticate with the entered password, then retry the email change
    const handleReauthAndRetry = async () => {
        setIsSaving(true);
        setMessage('');
        setIsError(false);
        try {
            const credential = EmailAuthProvider.credential(user.email, reauthPassword);
            await reauthenticateWithCredential(auth.currentUser, credential);
            await verifyBeforeUpdateEmail(auth.currentUser, email);
            setNeedsReauth(false);
            setReauthPassword('');
            setMessage(`A confirmation link was sent to ${email} — click it to finish changing your email.`);
            setIsError(false);
        } catch (error) {
            console.error("Error re-authenticating for email change:", error);
            setMessage(`Re-authentication failed: ${error.message}`);
            setIsError(true);
        }
        setIsSaving(false);
    };

    const handlePasswordReset = async () => {
        setMessage('');
        setIsError(false);
        try {
            await sendPasswordResetEmail(auth, user.email);
            setMessage("Password reset email sent! Please check your inbox.");
            setIsError(false);
        } catch (error) {
            console.error("Error sending password reset email:", error);
            setMessage("Failed to send password reset email. Please try again.");
            setIsError(true);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-xl font-bold mb-4">Your Profile</h2>
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Name</label>
                    <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full p-2 border border-gray-300 rounded-md" />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full p-2 border border-gray-300 rounded-md" />
                </div>
                {/* emailself-5e6f7081 — inline reauth prompt, shown only when Firebase asks for a recent login */}
                {needsReauth && (
                    <div className="p-3 border border-amber-300 bg-amber-50 rounded-md space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Confirm your password to change your email</label>
                        <input
                            type="password"
                            value={reauthPassword}
                            onChange={(e) => setReauthPassword(e.target.value)}
                            placeholder="Current password"
                            className="mt-1 w-full p-2 border border-gray-300 rounded-md"
                        />
                        <button
                            onClick={handleReauthAndRetry}
                            disabled={isSaving || !reauthPassword}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400"
                        >
                            {isSaving ? 'Confirming...' : 'Confirm & change email'}
                        </button>
                    </div>
                )}
                <div>
                    <label className="block text-sm font-medium text-gray-500">Role</label>
                    <p className="px-2 py-1 bg-gray-200 text-gray-800 rounded-full inline-block text-sm">{profile?.role || 'User'}</p>
                </div>
                <div className="flex items-center justify-between">
                    <button onClick={handlePasswordReset} className="text-sm text-blue-600 hover:underline">
                        Send Password Reset Email
                    </button>
                    <button onClick={handleProfileUpdate} disabled={isSaving} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                        {isSaving ? 'Saving...' : 'Save Profile'}
                    </button>
                </div>
            </div>
            {/* emailself-6f708192 — errors render red, success green (was always green) */}
            {message && <p className={`text-sm mt-4 ${isError ? 'text-red-600' : 'text-green-600'}`}>{message}</p>}
        </div>
    );
};

export default CurrentUserProfile;